import api from '@/services/apiService';
import { assertApiSuccess, type ApiResponse } from '@/types/api';
import type {
  CommunityMemberDetail,
  HostCandidate,
  HostCandidateListResponse,
  HostCandidateMeta,
  HostListResponse,
  HostProfile,
} from '@/types/communityHost';
import { getApiErrorMessage } from '@/utils/apiErrorMessage';

interface ApiMetaEnvelope<T> {
  success: boolean;
  message: string;
  data: T;
  meta?: HostCandidateMeta;
}

function fallbackCandidateMeta(): HostCandidateMeta {
  return { page: 1, limit: 20, total: 0, total_pages: 0 };
}

export async function listHosts(): Promise<HostListResponse> {
  const res = await api.get<ApiResponse<HostListResponse>>('/community/hosts');
  assertApiSuccess(res.data);
  const data = res.data.data;
  return {
    hosts: data.hosts ?? [],
    stats: {
      total_pengelola: data.stats?.total_pengelola ?? 0,
      co_host_aktif: data.stats?.co_host_aktif ?? 0,
    },
  };
}

export async function listCandidates(
  search = '',
  page = 1,
  limit = 20
): Promise<HostCandidateListResponse> {
  const res = await api.get<ApiMetaEnvelope<HostCandidate[]>>('/community/hosts/candidates', {
    params: { search, page, limit },
  });
  if (!res.data.success) {
    throw new Error(res.data.message || 'Gagal memuat kandidat co-host');
  }
  return {
    items: Array.isArray(res.data.data) ? res.data.data : [],
    meta: res.data.meta ?? fallbackCandidateMeta(),
  };
}

export async function addHost(communityUserId: number): Promise<void> {
  const res = await api.post<ApiResponse<null>>('/community/hosts', {
    community_user_id: communityUserId,
  });
  assertApiSuccess(res.data);
}

export async function removeHost(communityUserId: number): Promise<void> {
  const res = await api.delete<ApiResponse<null>>(`/community/hosts/${communityUserId}`);
  assertApiSuccess(res.data);
}

export async function getHostProfile(communityUserId: number): Promise<HostProfile> {
  try {
    const res = await api.get<ApiResponse<HostProfile>>(
      `/community/hosts/${communityUserId}/profile`
    );
    assertApiSuccess(res.data);
    return res.data.data;
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Gagal memuat profil pengelola'));
  }
}

export async function getHostMemberDetail(
  slug: string,
  memberId: number
): Promise<CommunityMemberDetail> {
  const res = await api.get<ApiResponse<CommunityMemberDetail>>(
    `/public/community/${slug}/members/${memberId}/detail`
  );
  assertApiSuccess(res.data);
  return res.data.data;
}
